import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Bot, Upload, FileText, X, Send, RefreshCw, CheckCircle,
  AlertTriangle, Search, Database, ChevronDown, ChevronUp
} from 'lucide-react';

const PROCESSING_STATUSES = new Set(['queued', 'extracting', 'interpreting', 'committing']);
const ACCEPTED_FILES = '.xlsx,.xls,.csv,.docx,.pdf,.txt';

function statusClasses(status) {
  if (status === 'completed') return 'bg-green-100 text-green-800';
  if (status === 'failed') return 'bg-red-100 text-red-800';
  if (status === 'needs_input') return 'bg-amber-100 text-amber-800';
  if (status === 'ready_for_review') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-700';
}

function ContactReviewTable({ analysis, onChange }) {
  const [search, setSearch] = useState('');
  const contacts = analysis?.contacts || [];
  const visibleContacts = contacts.filter(contact => {
    const text = [contact.full_name, contact.email, contact.company_name, contact.job_title]
      .filter(Boolean).join(' ').toLowerCase();
    return text.includes(search.toLowerCase().trim());
  });

  const updateContact = (stableId, fields) => {
    onChange({
      ...analysis,
      contacts: contacts.map(contact => contact.stable_id === stableId
        ? { ...contact, ...fields }
        : contact)
    });
  };

  const updateName = (contact, field, value) => {
    const firstName = field === 'first_name' ? value : contact.first_name;
    const lastName = field === 'last_name' ? value : contact.last_name;
    updateContact(contact.stable_id, {
      [field]: value || null,
      full_name: [firstName, lastName].filter(Boolean).join(' ') || null
    });
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="p-3 border-b bg-gray-50 flex items-center justify-between gap-3">
        <div>
          <h4 className="font-semibold text-gray-900">Contacts ({contacts.length})</h4>
          <p className="text-xs text-gray-500">A contact can be saved without being included in email delivery.</p>
        </div>
        <div className="relative w-72 max-w-full">
          <Search size={15} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search contacts..."
            className="w-full border rounded-md py-2 pl-9 pr-3 text-sm"
          />
        </div>
      </div>
      <div className="overflow-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white border-b text-left text-xs text-gray-500 uppercase">
            <tr>
              <th className="p-3">Contact</th>
              <th className="p-3">Company</th>
              <th className="p-3">Email state</th>
              <th className="p-3">Source</th>
              <th className="p-3">Import</th>
              <th className="p-3">Campaign</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visibleContacts.map(contact => (
              <tr key={contact.stable_id} className="align-top">
                <td className="p-3 min-w-48">
                  <div className="grid grid-cols-2 gap-1">
                    <input value={contact.first_name || ''} onChange={event => updateName(contact, 'first_name', event.target.value)} placeholder="First name" className="w-full border rounded px-2 py-1 text-xs" />
                    <input value={contact.last_name || ''} onChange={event => updateName(contact, 'last_name', event.target.value)} placeholder="Last name" className="w-full border rounded px-2 py-1 text-xs" />
                  </div>
                  <input value={contact.job_title || ''} onChange={event => updateContact(contact.stable_id, { job_title: event.target.value || null })} placeholder="Job title" className="mt-1 w-full border rounded px-2 py-1 text-xs" />
                  <input
                    value={contact.email || ''}
                    onChange={event => updateContact(contact.stable_id, { email: event.target.value || null })}
                    placeholder="No email"
                    className="mt-2 w-full border rounded px-2 py-1 text-xs"
                  />
                </td>
                <td className="p-3 min-w-40"><input value={contact.company_name || ''} onChange={event => updateContact(contact.stable_id, { company_name: event.target.value || null })} placeholder="No company" className="w-full border rounded px-2 py-1 text-xs" /></td>
                <td className="p-3 min-w-36">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs ${contact.email_readiness === 'ready' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {contact.email_readiness.replaceAll('_', ' ')}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">{contact.email_verification_status.replaceAll('_', ' ')}</div>
                </td>
                <td className="p-3 min-w-28 capitalize">{contact.origin_type}</td>
                <td className="p-3 min-w-36">
                  <select
                    value={contact.import_decision}
                    onChange={event => updateContact(contact.stable_id, { import_decision: event.target.value })}
                    className="border rounded px-2 py-1 text-xs w-full"
                  >
                    <option value="import">Save contact</option>
                    <option value="hold">Hold for review</option>
                    <option value="skip">Skip</option>
                  </select>
                </td>
                <td className="p-3 min-w-36">
                  <select
                    value={contact.campaign_inclusion}
                    disabled={contact.email_readiness !== 'ready'}
                    onChange={event => updateContact(contact.stable_id, { campaign_inclusion: event.target.value })}
                    className="border rounded px-2 py-1 text-xs w-full disabled:bg-gray-100"
                  >
                    <option value="include">Include</option>
                    <option value="exclude">Exclude</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EvidencePanel({ evidence = [] }) {
  const [open, setOpen] = useState(false);
  if (!evidence.length) return null;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen(value => !value)} className="text-xs text-blue-600 flex items-center gap-1">
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        Source evidence ({evidence.length})
      </button>
      {open && (
        <div className="mt-2 space-y-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
          {evidence.map((item, index) => (
            <div key={`${item.filename}-${item.locator}-${index}`}>
              {item.field_name ? `${item.field_name} · ` : ''}{item.filename || 'External source'} · {item.locator || 'No location'} · {item.confidence}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const AssistantWorkspace = () => {
  const [session, setSession] = useState(null);
  const [instruction, setInstruction] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [reviewAnalysis, setReviewAnalysis] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatClearing, setIsChatClearing] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);
  const fileInputRef = useRef(null);
  const chatMessagesEndRef = useRef(null);
  const chatGenerationRef = useRef(0);

  const loadSession = useCallback(async sessionId => {
    const response = await axios.get(`/api/assistant/imports/${sessionId}`);
    setSession(response.data.session);
    setReviewAnalysis(response.data.session.analysis || null);
    return response.data.session;
  }, []);

  useEffect(() => {
    if (session) return;
    axios.get('/api/assistant/imports?limit=8')
      .then(response => setRecentSessions(response.data.sessions || []))
      .catch(() => setRecentSessions([]));
  }, [session]);

  useEffect(() => {
    axios.get('/api/chat/history')
      .then(response => setChatMessages((response.data.messages || []).map(message => ({
        role: message.role,
        text: message.content
      }))))
      .catch(() => setChatMessages([]));
  }, []);

  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    const clearVisibleChat = () => {
      chatGenerationRef.current += 1;
      setChatMessages([]);
      setChatInput('');
    };
    window.addEventListener('promptcrm-chat-cleared', clearVisibleChat);
    return () => window.removeEventListener('promptcrm-chat-cleared', clearVisibleChat);
  }, []);

  useEffect(() => {
    if (!session?.id || !PROCESSING_STATUSES.has(session.status)) return undefined;
    const timer = setInterval(() => {
      loadSession(session.id).catch(requestError => {
        setError(requestError.response?.data?.error || 'The assistant status is unavailable.');
      });
    }, 1500);
    return () => clearInterval(timer);
  }, [session?.id, session?.status, loadSession]);

  const addFiles = files => {
    const accepted = Array.from(files || []).filter(file => {
      const extension = `.${file.name.split('.').pop().toLowerCase()}`;
      return ACCEPTED_FILES.split(',').includes(extension);
    });
    setSelectedFiles(current => {
      const keys = new Set(current.map(file => `${file.name}:${file.size}`));
      return [...current, ...accepted.filter(file => !keys.has(`${file.name}:${file.size}`))];
    });
  };

  const startImport = async () => {
    if (!selectedFiles.length && !instruction.trim()) {
      setError('Add a supported file or paste information for the assistant.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const createResponse = await axios.post('/api/assistant/imports', { instruction });
      const created = createResponse.data.session;
      if (selectedFiles.length) {
        const formData = new FormData();
        selectedFiles.forEach(file => formData.append('files', file));
        await axios.post(`/api/assistant/imports/${created.id}/files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      const analysisResponse = await axios.post(`/api/assistant/imports/${created.id}/analyze`, { instruction });
      setSession(analysisResponse.data.session);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'The document analysis could not start.');
    } finally {
      setBusy(false);
    }
  };

  const submitAnswers = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await axios.post(`/api/assistant/imports/${session.id}/answers`, { corrections: answers });
      setSession(response.data.session);
      setAnswers({});
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'The answers could not be applied.');
    } finally {
      setBusy(false);
    }
  };

  const retryAnalysis = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await axios.post(`/api/assistant/imports/${session.id}/analyze`, { instruction: session.instruction });
      setSession(response.data.session);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'The analysis could not restart.');
    } finally {
      setBusy(false);
    }
  };

  const cancelAnalysis = async () => {
    try {
      const response = await axios.post(`/api/assistant/imports/${session.id}/cancel`);
      setSession(response.data.session);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'The analysis could not be cancelled.');
    }
  };

  const saveReview = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await axios.put(`/api/assistant/imports/${session.id}/review`, { analysis: reviewAnalysis });
      setSession(response.data.session);
      setReviewAnalysis(response.data.session.analysis);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'The review could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const enrichMissing = async () => {
    setBusy(true);
    setError('');
    try {
      const ids = (reviewAnalysis?.contacts || [])
        .filter(contact => contact.email_readiness !== 'ready' && contact.import_decision !== 'skip')
        .map(contact => contact.stable_id);
      const response = await axios.post(`/api/assistant/imports/${session.id}/enrich`, { contact_ids: ids });
      setSession(response.data.session);
      setReviewAnalysis(response.data.session.analysis);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Email discovery could not finish.');
    } finally {
      setBusy(false);
    }
  };

  const researchProspects = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await axios.post(`/api/assistant/imports/${session.id}/research`);
      setSession(response.data.session);
      setReviewAnalysis(response.data.session.analysis);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Prospect research could not finish.');
    } finally {
      setBusy(false);
    }
  };

  const commitImport = async () => {
    setBusy(true);
    setError('');
    try {
      if (reviewAnalysis) {
        await axios.put(`/api/assistant/imports/${session.id}/review`, { analysis: reviewAnalysis });
      }
      const response = await axios.post(`/api/assistant/imports/${session.id}/commit`);
      setSession(response.data.session);
      setReviewAnalysis(response.data.session.analysis);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'The reviewed import could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const sendAssistantQuestion = async () => {
    const message = chatInput.trim();
    if (!message) return;
    const generation = chatGenerationRef.current;
    setChatMessages(items => [...items, { role: 'user', text: message }]);
    setChatInput('');
    try {
      const response = await axios.post('/api/chat/chat', { message });
      if (generation === chatGenerationRef.current) {
        setChatMessages(items => [...items, { role: 'assistant', text: response.data.message }]);
      }
    } catch (requestError) {
      if (generation === chatGenerationRef.current) {
        setChatMessages(items => [...items, { role: 'assistant', text: 'The assistant could not process that request.' }]);
      }
    }
  };

  const startNewChat = async () => {
    setIsChatClearing(true);
    try {
      await axios.delete('/api/chat/history');
      window.dispatchEvent(new Event('promptcrm-chat-cleared'));
    } catch (requestError) {
      setChatMessages(items => [...items, {
        role: 'assistant',
        text: 'The chat history could not be cleared.'
      }]);
    } finally {
      setIsChatClearing(false);
    }
  };

  const missingEnrichmentCount = useMemo(() => (reviewAnalysis?.contacts || [])
    .filter(contact => contact.email_readiness !== 'ready'
      && contact.enrichment_status !== 'not_requested'
      && contact.import_decision !== 'skip').length, [reviewAnalysis]);
  const openBlockingItems = useMemo(() => (reviewAnalysis?.unresolved_items || [])
    .filter(item => item.severity === 'blocking' && !item.user_resolution), [reviewAnalysis]);
  const duplicateMatches = session?.review_context?.duplicate_matches || [];

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Bot size={26} /> Assistant</h2>
        <p className="text-gray-600">Ask about Prompt CRM or turn campaign and research files into reviewed CRM records.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {!session && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white border rounded-xl p-5 space-y-4 self-start">
            <div>
              <h3 className="font-semibold text-lg">Import campaign or research files</h3>
              <p className="text-sm text-gray-500">Excel, CSV, Word, text PDF, scanned PDF, or plain text.</p>
            </div>
            <textarea
              value={instruction}
              onChange={event => setInstruction(event.target.value)}
              rows={4}
              placeholder="Describe the task, or paste campaign and prospect information here. For example: Build a draft campaign from these files. Keep contacts without email."
              className="w-full border rounded-lg p-3 resize-y"
            />
            <div
              onDragEnter={event => { event.preventDefault(); setIsDragging(true); }}
              onDragOver={event => event.preventDefault()}
              onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setIsDragging(false); }}
              onDrop={event => { event.preventDefault(); setIsDragging(false); addFiles(event.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}
            >
              <Upload className="mx-auto text-blue-600 mb-2" size={30} />
              <div className="font-medium">{isDragging ? 'Drop files here' : 'Click to upload or drag and drop'}</div>
              <div className="text-xs text-gray-500 mt-1">Up to 10 files, 25 MB each</div>
              <input ref={fileInputRef} type="file" multiple accept={ACCEPTED_FILES} className="hidden" onChange={event => addFiles(event.target.files)} />
            </div>
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                {selectedFiles.map(file => (
                  <div key={`${file.name}:${file.size}`} className="flex items-center justify-between bg-gray-50 border rounded-lg px-3 py-2">
                    <span className="flex items-center gap-2 text-sm"><FileText size={16} /> {file.name}</span>
                    <button type="button" onClick={() => setSelectedFiles(files => files.filter(item => item !== file))} className="text-gray-400 hover:text-red-600"><X size={16} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button type="button" disabled={busy || (!selectedFiles.length && !instruction.trim())} onClick={startImport} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-5 py-2.5 rounded-lg flex items-center gap-2">
                {busy ? <RefreshCw size={17} className="animate-spin" /> : <Bot size={17} />} Analyze files
              </button>
            </div>
            {recentSessions.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-sm">Recent imports</h4>
                <div className="mt-2 space-y-2">
                  {recentSessions.map(item => (
                    <button key={item.id} type="button" onClick={() => loadSession(item.id)} className="w-full border rounded-lg p-3 text-left hover:bg-gray-50 flex items-center justify-between gap-3">
                      <span className="text-sm truncate">{item.files?.[0]?.filename || item.instruction || 'Document import'}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${statusClasses(item.status)}`}>{item.status.replaceAll('_', ' ')}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border rounded-xl flex flex-col min-h-96 lg:h-[calc(100vh-12rem)]">
            <div className="p-4 border-b flex items-start justify-between gap-3">
              <div><h3 className="font-semibold">Ask Prompt CRM</h3><p className="text-xs text-gray-500">Contacts, campaigns, metrics, and app help.</p></div>
              <button type="button" onClick={startNewChat} disabled={isChatClearing} className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 whitespace-nowrap">
                {isChatClearing ? 'Clearing...' : 'New chat'}
              </button>
            </div>
            <div className="flex-1 min-h-0 p-3 space-y-2 overflow-auto">
              {chatMessages.length === 0 && <p className="text-sm text-gray-400 text-center mt-12">Ask a question about your CRM.</p>}
              {chatMessages.map((message, index) => (
                <div key={index} className={`text-sm rounded-lg p-2 ${message.role === 'user' ? 'bg-blue-600 text-white ml-8' : 'bg-gray-100 mr-8'}`}>{message.text}</div>
              ))}
              <div ref={chatMessagesEndRef} />
            </div>
            <div className="p-3 border-t flex gap-2">
              <input value={chatInput} onChange={event => setChatInput(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') sendAssistantQuestion(); }} placeholder="Ask a question..." className="flex-1 border rounded-lg px-3 py-2 text-sm" />
              <button type="button" onClick={sendAssistantQuestion} className="bg-blue-600 text-white rounded-lg p-2"><Send size={18} /></button>
            </div>
          </div>
        </div>
      )}

      {session && (
        <div className="space-y-5">
          <div className="bg-white border rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg">Document import</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${statusClasses(session.status)}`}>{session.status.replaceAll('_', ' ')}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{session.progress_message}</p>
              </div>
              <button type="button" onClick={() => { setSession(null); setReviewAnalysis(null); setSelectedFiles([]); setError(''); }} className="text-sm text-blue-600">Start another</button>
            </div>
            <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${session.progress}%` }} /></div>
            <div className="mt-3 flex flex-wrap gap-2">{session.files.map(file => <span key={file.id} className="text-xs bg-gray-100 rounded px-2 py-1">{file.filename}</span>)}</div>
            {PROCESSING_STATUSES.has(session.status) && <button type="button" onClick={cancelAnalysis} className="mt-3 text-sm text-red-600">Cancel analysis</button>}
            {session.status === 'failed' && <button type="button" disabled={busy} onClick={retryAnalysis} className="mt-3 border rounded-lg px-4 py-2 text-sm">Try analysis again</button>}
          </div>

          {session.status === 'needs_input' && reviewAnalysis && (
            <div className="bg-white border rounded-xl p-5 space-y-4">
              <h3 className="font-semibold text-lg">The assistant needs your input</h3>
              {reviewAnalysis.clarification_questions.map(question => (
                <div key={question.id} className="border rounded-lg p-4">
                  <label className="font-medium text-gray-900">{question.question}</label>
                  <p className="text-xs text-gray-500 mt-1">{question.reason}</p>
                  {question.choices.length > 0 ? (
                    <select value={answers[question.id] || ''} onChange={event => setAnswers(value => ({ ...value, [question.id]: event.target.value }))} className="mt-3 border rounded-lg p-2 w-full max-w-xl">
                      <option value="">Select an answer</option>
                      {question.choices.map(choice => <option key={choice} value={choice}>{choice}</option>)}
                    </select>
                  ) : (
                    <input value={answers[question.id] || ''} onChange={event => setAnswers(value => ({ ...value, [question.id]: event.target.value }))} className="mt-3 border rounded-lg p-2 w-full" />
                  )}
                </div>
              ))}
              <div className="flex justify-end"><button type="button" disabled={busy} onClick={submitAnswers} className="bg-blue-600 text-white rounded-lg px-5 py-2">Apply answers</button></div>
            </div>
          )}

          {['ready_for_review', 'completed'].includes(session.status) && reviewAnalysis && (
            <>
              <div className="bg-white border rounded-xl p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-lg">Analysis summary</h3>
                    <p className="text-sm text-gray-600 mt-1">{reviewAnalysis.summary.statement}</p>
                  </div>
                  <span className="text-xs uppercase tracking-wide text-gray-500">{reviewAnalysis.classification.replaceAll('_', ' ')}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {[
                    ['Campaigns', reviewAnalysis.summary.campaigns_found], ['Accounts', reviewAnalysis.summary.accounts_found],
                    ['Contacts', reviewAnalysis.summary.contacts_found], ['Valid emails', reviewAnalysis.summary.valid_email_count],
                    ['Steps', reviewAnalysis.summary.sequence_steps_found], ['Warnings', reviewAnalysis.summary.warning_count]
                  ].map(([label, value]) => <div key={label} className="border rounded-lg p-3 text-center"><div className="text-xl font-bold">{value}</div><div className="text-xs text-gray-500">{label}</div></div>)}
                </div>
              </div>

              {reviewAnalysis.campaigns.map(campaign => (
                <div key={campaign.stable_id} className="bg-white border rounded-xl p-5">
                  <h3 className="font-semibold text-lg">Draft campaign</h3>
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <label className="text-sm">Name<input value={campaign.name || ''} onChange={event => setReviewAnalysis(value => ({ ...value, campaigns: value.campaigns.map(item => item.stable_id === campaign.stable_id ? { ...item, name: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1" /></label>
                    <label className="text-sm">Timezone<input value={campaign.timezone || ''} onChange={event => setReviewAnalysis(value => ({ ...value, campaigns: value.campaigns.map(item => item.stable_id === campaign.stable_id ? { ...item, timezone: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1" /></label>
                    <label className="text-sm">Sender email<input value={campaign.sender_identity || ''} onChange={event => setReviewAnalysis(value => ({ ...value, campaigns: value.campaigns.map(item => item.stable_id === campaign.stable_id ? { ...item, sender_identity: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1" /></label>
                    <label className="text-sm">Start date from source<input value={campaign.start_date || ''} onChange={event => setReviewAnalysis(value => ({ ...value, campaigns: value.campaigns.map(item => item.stable_id === campaign.stable_id ? { ...item, start_date: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1" /></label>
                    <label className="text-sm md:col-span-2">Objective<textarea value={campaign.objective || ''} onChange={event => setReviewAnalysis(value => ({ ...value, campaigns: value.campaigns.map(item => item.stable_id === campaign.stable_id ? { ...item, objective: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1 resize-y" /></label>
                    <p className="text-xs text-gray-500 md:col-span-2">The imported campaign remains a draft. The source start date is context only and does not schedule delivery.</p>
                  </div>
                  <EvidencePanel evidence={campaign.source_evidence} />
                </div>
              ))}

              {reviewAnalysis.accounts.length > 0 && (
                <div className="bg-white border rounded-xl p-5">
                  <h3 className="font-semibold text-lg">Accounts and research ({reviewAnalysis.accounts.length})</h3>
                  <div className="mt-3 grid md:grid-cols-2 gap-3">
                    {reviewAnalysis.accounts.map(account => (
                      <div key={account.stable_id} className="border rounded-lg p-3">
                        <input value={account.company_name || ''} onChange={event => setReviewAnalysis(value => ({ ...value, accounts: value.accounts.map(item => item.stable_id === account.stable_id ? { ...item, company_name: event.target.value } : item) }))} className="font-medium border rounded p-2 w-full" />
                        <textarea value={account.research_context || ''} onChange={event => setReviewAnalysis(value => ({ ...value, accounts: value.accounts.map(item => item.stable_id === account.stable_id ? { ...item, research_context: event.target.value || null } : item) }))} placeholder="Research context" className="mt-2 border rounded p-2 w-full text-sm resize-y" />
                        <EvidencePanel evidence={account.source_evidence} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {duplicateMatches.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                  <h3 className="font-semibold text-lg">Existing CRM matches ({duplicateMatches.length})</h3>
                  <p className="text-sm text-gray-600 mt-1">Matching contacts and accounts will be updated. They will not be added as duplicates.</p>
                  <div className="mt-3 flex flex-wrap gap-2">{duplicateMatches.map((match, index) => <span key={`${match.entity_type}-${match.existing_id}-${index}`} className="bg-white border rounded px-2 py-1 text-xs">{match.entity_type}: {match.value}</span>)}</div>
                </div>
              )}

              <div className="bg-white border rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-lg">Sequence ({reviewAnalysis.sequence_steps.length} steps)</h3>
                {[...reviewAnalysis.sequence_steps].sort((a, b) => a.step_number - b.step_number).map(step => (
                  <details key={step.stable_id} className="border rounded-lg p-3">
                    <summary className="cursor-pointer font-medium">{step.step_number === 1 ? 'Initial email' : `Follow-up ${step.step_number}`} · {step.subject_default || 'No subject'}</summary>
                    <div className="grid md:grid-cols-2 gap-3 mt-3 text-sm">
                      {reviewAnalysis.campaigns.length > 1 && <label>Campaign<select value={step.campaign_stable_id || ''} onChange={event => setReviewAnalysis(value => ({ ...value, sequence_steps: value.sequence_steps.map(item => item.stable_id === step.stable_id ? { ...item, campaign_stable_id: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1"><option value="">Select a campaign</option>{reviewAnalysis.campaigns.map(campaign => <option key={campaign.stable_id} value={campaign.stable_id}>{campaign.name || campaign.stable_id}</option>)}</select></label>}
                      <label>Subject<input value={step.subject_default || ''} onChange={event => setReviewAnalysis(value => ({ ...value, sequence_steps: value.sequence_steps.map(item => item.stable_id === step.stable_id ? { ...item, subject_default: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1" /></label>
                      <label>Channel<select value={step.channel} onChange={event => setReviewAnalysis(value => ({ ...value, sequence_steps: value.sequence_steps.map(item => item.stable_id === step.stable_id ? { ...item, channel: event.target.value } : item) }))} className="block w-full border rounded p-2 mt-1"><option value="email">Email</option><option value="linkedin">LinkedIn</option><option value="other">Other</option></select></label>
                      <label>Delay amount<input type="number" min="0" value={step.offset_amount ?? ''} onChange={event => setReviewAnalysis(value => ({ ...value, sequence_steps: value.sequence_steps.map(item => item.stable_id === step.stable_id ? { ...item, offset_amount: event.target.value === '' ? null : Number(event.target.value) } : item) }))} className="block w-full border rounded p-2 mt-1" /></label>
                      <label>Delay unit<select value={step.offset_unit || ''} onChange={event => setReviewAnalysis(value => ({ ...value, sequence_steps: value.sequence_steps.map(item => item.stable_id === step.stable_id ? { ...item, offset_unit: event.target.value || null } : item) }))} className="block w-full border rounded p-2 mt-1"><option value="">Not set</option><option value="hours">Hours</option><option value="days">Days</option><option value="weeks">Weeks</option><option value="months">Months</option></select></label>
                      <label className="md:col-span-2">Body<textarea value={step.body_copy || ''} onChange={event => setReviewAnalysis(value => ({ ...value, sequence_steps: value.sequence_steps.map(item => item.stable_id === step.stable_id ? { ...item, body_copy: event.target.value || null } : item) }))} rows={8} className="block w-full border rounded p-3 mt-1 resize-y" /></label>
                    </div>
                    <EvidencePanel evidence={step.source_evidence} />
                  </details>
                ))}
              </div>

              <div className="bg-white border rounded-xl p-5"><ContactReviewTable analysis={reviewAnalysis} onChange={setReviewAnalysis} /></div>

              {reviewAnalysis.unresolved_items.length > 0 && (
                <div className="bg-white border rounded-xl p-5">
                  <h3 className="font-semibold text-lg">Unresolved items ({reviewAnalysis.unresolved_items.length})</h3>
                  <div className="mt-3 space-y-2">{reviewAnalysis.unresolved_items.map(item => <div key={item.stable_id} className="border rounded-lg p-3 text-sm"><span className={`font-medium ${item.severity === 'blocking' ? 'text-red-600' : 'text-amber-700'}`}>{item.severity}:</span> {item.reason}{item.suggested_choices.length > 0 && <select value={item.user_resolution || ''} onChange={event => setReviewAnalysis(value => ({ ...value, unresolved_items: value.unresolved_items.map(issue => issue.stable_id === item.stable_id ? { ...issue, user_resolution: event.target.value || null } : issue) }))} className="block border rounded p-2 mt-2 w-full max-w-xl"><option value="">Select a resolution</option>{item.suggested_choices.map(choice => <option key={choice} value={choice}>{choice}</option>)}</select>}<EvidencePanel evidence={item.source_evidence} /></div>)}</div>
                </div>
              )}

              {session.status === 'ready_for_review' && (
                <div className="sticky bottom-4 bg-white border shadow-lg rounded-xl p-4 flex flex-wrap justify-between items-center gap-3">
                  <div className="text-sm text-gray-600 flex items-center gap-2"><Database size={17} /> Nothing is written until you confirm.</div>
                  <div className="flex flex-wrap gap-2">
                    {reviewAnalysis.intent.external_enrichment_allowed && <button type="button" disabled={busy} onClick={researchProspects} className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg">Research prospects</button>}
                    {missingEnrichmentCount > 0 && reviewAnalysis.intent.email_discovery_allowed && <button type="button" disabled={busy} onClick={enrichMissing} className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg">Find missing emails</button>}
                    <button type="button" disabled={busy} onClick={saveReview} className="border px-4 py-2 rounded-lg">Save review</button>
                    <button type="button" disabled={busy || openBlockingItems.length > 0} onClick={commitImport} className="bg-blue-600 disabled:bg-gray-300 text-white px-5 py-2 rounded-lg">Create draft campaign / import records</button>
                  </div>
                </div>
              )}
            </>
          )}

          {session.status === 'completed' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-start gap-3">
              <CheckCircle className="text-green-600" />
              <div><h3 className="font-semibold text-green-900">Import complete</h3><p className="text-sm text-green-800 mt-1">Created: {session.result?.created || 0}. Updated: {session.result?.updated || 0}. Skipped: {session.result?.skipped || 0}. Unresolved: {session.result?.unresolved || 0}.</p></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssistantWorkspace;
